// Shared visual language for the UFC fighter panel — used by:
//   • PickemMode FighterCorner       (pickem hero card, interactive)
//   • App.jsx UFCFighterSide         (Games-tab featured event card)
//   • App.jsx UFCMiniFighterSide     (Games-tab "Later this week" small card)
//
// Two surfaces, one visual language. Tuning happens here once so the
// PickemMode bolden pass (c0b527c → 8293476-era values) and any future
// adjustments flow to both consumers without re-deriving.
//
// Pure constants + string builders + flag palette lookup. No React,
// no JSX, no DOM. The accompanying component
// `src/components/FighterPanelLayers.jsx` renders the layered
// absolute-positioned divs using the helpers below; each consumer
// wraps the layers in its own container (button vs div, with its own
// border/padding/content) so the surfaces can stay structurally
// distinct while sharing the visual treatment.

export const UFC_CORNER_RED  = '#e23b3b'
export const UFC_CORNER_BLUE = '#2f7fd6'

// Per-country flag palette — colors[0] is the structural accent
// (border / ring / underline at consumer sites), colors[1+] feed the
// radial-gradient glow stops. Falls back to the cornerColor when a
// country isn't in this map (catches ESPN strings we haven't seen yet).
export const FLAG_PALETTES = {
  USA:                ['#bf0a30', '#002868', '#ffffff'],
  Brazil:             ['#009b3a', '#fedf00', '#002776'],
  Canada:             ['#d80027', '#ffffff'],
  Mexico:             ['#006847', '#ffffff', '#ce1126'],
  France:             ['#0055a4', '#ed2939', '#ffffff'],
  Spain:              ['#aa151b', '#f1bf00'],
  Italy:              ['#008c45', '#cd212a', '#ffffff'],
  Germany:            ['#dd0000', '#000000', '#ffce00'],
  England:            ['#ce1124', '#ffffff'],
  Ireland:            ['#169b62', '#ffffff', '#ff883e'],
  Scotland:           ['#0065bd', '#ffffff'],
  'Northern Ireland': ['#ce1124', '#ffffff'],
  Wales:              ['#d30731', '#00ab39', '#ffffff'],
  Russia:             ['#0033a0', '#d52b1e', '#ffffff'],
  Poland:             ['#dc143c', '#ffffff'],
  Netherlands:        ['#ae1c28', '#ffffff', '#21468b'],
  Sweden:             ['#006aa7', '#fecc02'],
  Norway:             ['#ba0c2f', '#00205b'],
  Denmark:            ['#c60c30', '#ffffff'],
  Switzerland:        ['#d52b1e', '#ffffff'],
  Belgium:            ['#fdda24', '#000000', '#ef3340'],
  Croatia:            ['#ff0000', '#ffffff', '#0066b3'],
  Czechia:            ['#11457e', '#ffffff', '#d7141a'],
  Georgia:            ['#ff0000', '#ffffff'],
  Ukraine:            ['#0057b7', '#ffdd00'],
  Kazakhstan:         ['#00afca', '#fec50c'],
  Australia:          ['#012169', '#e4002b', '#ffffff'],
  'New Zealand':      ['#012169', '#cc142b', '#ffffff'],
  Japan:              ['#bc002d', '#ffffff'],
  'South Korea':      ['#003478', '#cd2e3a', '#ffffff'],
  China:              ['#de2910', '#ffde00'],
  Argentina:          ['#74acdf', '#ffffff', '#f6b40e'],
  Cuba:               ['#cf142b', '#002a8f', '#ffffff'],
  Nigeria:            ['#008751', '#ffffff'],
  'South Africa':     ['#007a4d', '#ffb612', '#de3831'],
  Cameroon:           ['#007a5e', '#ce1126', '#fcd116'],
  Suriname:           ['#377e3f', '#b40a2d', '#ffffff'],
}

export function getFlagPalette(country) {
  if (!country) return null
  return FLAG_PALETTES[country] || null
}

// Local hexToRGBA — duplicated from the existing copies in
// PickemMode.jsx and App.jsx to keep this module a single import
// surface for both consumers without forcing a refactor of the
// existing local copies (out of scope here).
function hexToRGBA(hex, alpha) {
  const h = hex.replace('#', '')
  const r = parseInt(h.slice(0, 2), 16)
  const g = parseInt(h.slice(2, 4), 16)
  const b = parseInt(h.slice(4, 6), 16)
  return `rgba(${r},${g},${b},${alpha})`
}

// Outer-container background — soft corner-color tint fading through
// dark to near-black at the bottom, on top of a solid panel base
// (#11151c). Used as the `background` CSS on the OUTER panel
// container; the layer stack (FighterPanelLayers) renders on top.
//
// Matches the PickemMode FighterCorner cornerTintBg exactly so the
// red-corner / blue-corner ambient is identical across surfaces.
export function cornerTintBg(cornerColor) {
  return `linear-gradient(160deg, ${hexToRGBA(cornerColor, 0.32)} 0%, rgba(13,16,22,0.92) 70%, rgba(13,16,22,0.98) 100%), #11151c`
}

// Country-tinted radial glow for the z:2 layer. Reads the flag palette
// via getFlagPalette(country) and builds a multi-stop radial gradient
// in the corner. Falls back to a single-color glow keyed on
// cornerColor when no palette is available (unknown country or
// missing flag in ESPN payload).
//
// Stops match PickemMode FighterCorner exactly (0.55 → 0.40 → 0.25,
// transparent at 78%) so the screen-blended ambient reads identically
// across surfaces.
export function buildCountryGlow({ country, side, cornerColor }) {
  const palette = getFlagPalette(country)
  const glowPos = side === 'left' ? '20% 30%' : '80% 30%'
  if (palette) {
    return `radial-gradient(85% 75% at ${glowPos},
      ${hexToRGBA(palette[0], 0.55)} 0%,
      ${hexToRGBA(palette[1] || palette[0], 0.40)} 35%,
      ${hexToRGBA(palette[2] || palette[1] || palette[0], 0.25)} 55%,
      rgba(0,0,0,0) 78%)`
  }
  return `radial-gradient(80% 70% at ${glowPos}, ${hexToRGBA(cornerColor, 0.55)} 0%, rgba(0,0,0,0) 60%)`
}
